import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/contexts/AuthContext";

// Downloads through the configured authenticated client, never a bare
// window.open URL. This does not confirm that a browser saved to disk.
export default function useAuthenticatedDownload() {
  const { user } = useAuth();
  const identity = `${user?.id || ""}:${user?.active_workspace || user?.id || ""}`;
  const currentIdentity = useRef(identity);
  currentIdentity.current = identity;
  const mounted = useRef(true);
  const request = useRef(null);
  const inFlight = useRef(false);
  const generation = useRef(0);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      generation.current += 1;
      request.current?.abort();
    };
  }, [identity]);

  const download = async (resourcePath, filename, expectedType) => {
    if (inFlight.current) return;
    if (!resourcePath.startsWith("/") || resourcePath.startsWith("//") || resourcePath.includes("://")) {
      toast.error("The download address is invalid.");
      return;
    }
    inFlight.current = true;
    setPending(true);
    const startedIdentity = identity;
    const epoch = generation.current;
    const controller = new AbortController();
    request.current = controller;
    try {
      const response = await axios.get(API + resourcePath, {
        responseType: "blob", signal: controller.signal,
      });
      if (!mounted.current || currentIdentity.current !== startedIdentity || epoch !== generation.current) return;
      const type = String(response.headers["content-type"] || "").split(";")[0].trim().toLowerCase();
      if (!(response.data instanceof Blob) || !response.data.size || type !== expectedType) {
        throw new Error("Unexpected download response");
      }
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      try {
        anchor.href = url;
        anchor.download = filename.replace(/[^a-zA-Z0-9._-]/g, "-");
        document.body.appendChild(anchor);
        anchor.click();
      } finally {
        anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch {
      if (!controller.signal.aborted && mounted.current && currentIdentity.current === startedIdentity) {
        toast.error("The file could not be downloaded. Check your access and try again.");
      }
    } finally {
      inFlight.current = false;
      request.current = null;
      if (mounted.current) setPending(false);
    }
  };
  return { download, pending };
}
